import api from "app/api";

export interface AccessControlSettings {
  enabled: boolean;
}

export type AccessControlEntryType = "ip_whitelist" | "ip_blacklist" | "country_whitelist";

export interface AccessControlEntry {
  id: number;
  type: AccessControlEntryType;
  value: string;
  label: string | null;
  expires_at?: string | null;
}

export interface AccessControlChanges {
  enabled?: boolean;
  additions: Omit<AccessControlEntry, "id">[];
  deletions: number[];
  updates?: { id: number; expires_at: string | null }[];
}

export interface AccessControlResponse {
  settings: AccessControlSettings;
  entries: AccessControlEntry[];
  can_manage: boolean;
  current_ip: string;
  update_days: number;
  countries: { code: string; name: string }[];
  database: {
    available: boolean;
    downloaded_at: string | null;
    build_at: string | null;
  };
}

export const loadAccessControl = () =>
  api.get<AccessControlResponse>("/access-control-settings");

export const saveAccessControl = (changes: AccessControlChanges) =>
  api.put<AccessControlResponse>("/access-control-settings", changes);

export function buildAccessControlChanges(
  original: AccessControlResponse, enabled: boolean, entries: AccessControlEntry[],
): AccessControlChanges {
  const retainedIds = new Set(entries.map(entry => entry.id));
  const originals = new Map(original.entries.map(entry => [entry.id, entry]));
  const updates = entries.filter(entry => entry.id > 0 && entry.type === "country_whitelist"
    && !sameExpiry(originals.get(entry.id)?.expires_at, entry.expires_at))
    .map(entry => ({ id: entry.id, expires_at: entry.expires_at ?? null }));
  const changes: AccessControlChanges = {
    additions: entries.filter(entry => entry.id < 0).map(({ type, value, label, expires_at }) => ({
      type, value, label, ...(expires_at ? { expires_at } : {}),
    })),
    deletions: original.entries.filter(entry => !retainedIds.has(entry.id)).map(entry => entry.id),
  };
  if (updates.length) changes.updates = updates;
  if (original.can_manage && enabled !== original.settings.enabled) changes.enabled = enabled;
  return changes;
}

export interface AccessControlHistory {
  data: {
    id: number;
    created_at: string;
    user_id: number;
    user_name: string | null;
    event: string;
    class_name: string;
    item_label: string;
    data: { id: string; old: unknown; new: unknown }[];
  }[];
  current_page: number;
  last_page: number;
  total: number;
}

export const loadAccessControlHistory = (page: number) =>
  api.get<AccessControlHistory>("/access-control-settings/history", { params: { page } });

export const validateAccessEntry = (type: "ip" | "country", value: string, label?: string, expires_at?: string | null) =>
  api.post<{ valid: boolean }>("/access-control-settings/validate-entry", {
    type, value, label, ...(expires_at ? { expires_at } : {}),
  });

export function localExpiryInput(value?: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}T${
    String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

export function expiryFromInput(value: string): string | null {
  return value ? new Date(value).toISOString().replace(/\.\d{3}Z$/, "Z") : null;
}

export function sameExpiry(left?: string | null, right?: string | null): boolean {
  return left && right ? new Date(left).getTime() === new Date(right).getTime() : !left && !right;
}

export function accessControlError(error: unknown, fallback: string): string {
  if (typeof error === "string") return error;
  if (Array.isArray(error)) return error.map(value => accessControlError(value, fallback)).join("\n");
  if (error && typeof error === "object") {
    return Object.values(error).map(value => accessControlError(value, fallback)).join("\n") || fallback;
  }
  return fallback;
}
